<template>
  <div class="w-full">
    <div
      class="flex gap-6 border-b border-steel-300 bg-white px-3 pb-4 md:border-none"
    >
      <span
        class="cursor-pointer"
        :class="{
          'border-b-2 border-sun-700 text-sun-700': selectedTab === 'Active',
          'text-base text-steel-700': selectedTab !== 'Active',
          'flex-1': !isDesktop,
        }"
        @click="emit('handle-tab-selection', 'Active')"
      >
        Active
      </span>
      <span
        class="cursor-pointer"
        :class="{
          'border-b-2 border-sun-700 text-sun-700': isExpired,
          'text-base text-steel-700': !isExpired,
          'flex-1': !isDesktop,
        }"
        @click="emit('handle-tab-selection', 'Expired')"
      >
        Expired
      </span>
    </div>

    <div class="px-3">
      <div v-if="isDesktop">
        <div
          class="grid grid-cols-14 rounded-t-lg border border-steel-300 bg-steel-100 px-2 py-3"
        >
          <div class="col-span-2 text-start text-xs">Truck Number</div>
          <div class="col-span-2 text-start text-xs">Origin</div>
          <div class="col-span-2 text-start text-xs">Destination</div>
          <div class="col-span-2 text-start text-xs">Equipment</div>
          <div class="col-span-2 text-start text-xs">Available Date</div>
          <div class="col-span-2 text-start text-xs">Posted Date</div>
          <div class="col-span-2 text-xs"></div>
        </div>

        <div
          v-if="is_truck_posting_loading"
          class="grid min-h-[500px] items-center border border-steel-300 px-2"
        >
          <img
            class="loading m-auto my-8 h-12 w-12"
            src="@/assets/loading-v2-yellow.svg"
            alt="loading"
          />
        </div>

        <div
          v-else-if="trucks?.length > 0"
          class="max-h-[calc(100vh-330px)] overflow-scroll"
        >
          <div
            v-for="truck in trucks"
            :key="truck.id"
            class="grid grid-cols-14 items-center border border-steel-300 px-2"
            :class="{
              'cursor-pointer hover:bg-sun-100': !isExpired,
            }"
          >
            <div
              class="col-span-2 flex flex-col items-start gap-1 py-3"
              @click="handleTruckSelection(truck)"
            >
              <p class="text-sm font-normal text-sun-700 underline">
                Truck: {{ truck?.carrier_vehicle?.id }}
              </p>
              <p class="text-xs font-light text-steel-700">
                VIN: {{ truck.carrier_vehicle?.vehicle?.vin }}
              </p>
              <p class="text-left text-xs font-light text-steel-700">
                {{ truck.carrier_vehicle?.vehicle?.model_year }}
                {{ truck.carrier_vehicle?.vehicle?.make }}
                {{ truck.carrier_vehicle?.vehicle?.model }}
              </p>
            </div>

            <div
              class="col-span-2 flex flex-col items-start py-3"
              @click="handleTruckSelection(truck)"
            >
              <p class="text-left text-sm font-light text-steel-900">
                {{ truck.origin_search_display_name || '-' }}
              </p>
              <p class="text-sm font-light text-steel-900">
                {{ truck.origin_area?.name || '' }}
              </p>
            </div>

            <div
              class="col-span-2 flex flex-col items-start py-3"
              @click="handleTruckSelection(truck)"
            >
              <p class="text-sm font-light text-steel-900">
                {{ truck.destination_search_display_name || '-' }}
              </p>
              <p class="text-sm font-light text-steel-900">
                {{ truck.destination_area?.name || '' }}
              </p>
            </div>

            <div
              class="col-span-2 py-3 text-start"
              @click="handleTruckSelection(truck)"
            >
              <p
                class="max-w-max rounded-full bg-ocean-200 px-2 py-[2px] text-sm capitalize text-ocean-700"
              >
                {{ truck.posted_equipment_type?.replace(/_/, ' ') }}
              </p>
            </div>

            <div
              class="col-span-2 py-3 text-start"
              @click="handleTruckSelection(truck)"
            >
              <p class="text-sm font-light text-steel-900">
                {{ formatPickup(truck) }}
              </p>
            </div>

            <div
              class="col-span-2 py-3 text-start"
              @click="handleTruckSelection(truck)"
            >
              <p class="text-sm font-light text-steel-900">
                {{ dayjs(truck.created_at).format('MMM DD') }}
              </p>
            </div>

            <div
              v-if="!isExpired"
              class="col-span-2 flex content-center gap-2 py-3 text-sm font-light font-normal capitalize text-steel-900"
            >
              <ButtonComponent @click="toggleEditModal(truck)">
                Edit
              </ButtonComponent>
              <ButtonComponent @click="toggleRemoveModal(truck)">
                Remove
              </ButtonComponent>
            </div>
          </div>
        </div>

        <div
          v-else
          class="flex min-h-[500px] items-center justify-center border border-steel-300"
        >
          <p class="text-lg text-steel-700">
            {{
              isExpired
                ? 'You have no expired trucks at this time.'
                : 'You have no posted trucks at this time.'
            }}
          </p>
        </div>

        <div
          v-if="trucks?.length > 0"
          class="relative flex min-h-[60px] items-center justify-center rounded-b-lg border border-steel-300 px-2 py-3"
        >
          <p class="absolute left-2 text-xs font-medium text-steel-700">
            Showing {{ trucks?.length }} of
            {{ truck_pagination?.totalRecords }} results
          </p>

          <pagination
            v-if="trucks?.length && truck_pagination?.totalPages > 1"
            :selected-page="truck_pagination?.currentPage"
            :total-pages="truck_pagination?.totalPages"
            :alignment="'center'"
            @select-page="onSelectPage"
          />
        </div>
      </div>

      <div
        v-else
        class="pt-4"
      >
        <div
          v-if="is_truck_posting_loading"
          class="min-h-[500px] items-center px-2"
        >
          <img
            class="loading m-auto my-8 h-12 w-12"
            src="@/assets/loading-v2-yellow.svg"
            alt="loading"
          />
        </div>
        <div
          v-else-if="trucks?.length > 0"
          class="mb-8 flex flex-wrap justify-center gap-4"
        >
          <p class="w-full text-left text-xs font-medium text-steel-700">
            Showing {{ trucks?.length }} of
            {{ truck_pagination?.totalRecords }}
            Truck{{ trucks?.length === 1 ? '' : 's' }}
          </p>

          <div
            v-for="truck in trucks"
            :key="truck.id"
            class="flex w-full max-w-[400px] flex-col rounded-lg border border-steel-200 bg-white p-3 text-left shadow-md"
          >
            <p class="text-base text-steel-1100">
              Truck: {{ truck?.carrier_vehicle?.id }}
            </p>
            <p class="text-xs font-normal text-steel-700">
              VIN: {{ truck.carrier_vehicle?.vehicle?.vin }}
            </p>
            <p class="text-xs font-normal text-steel-700">
              {{ dayjs(truck.created_at).format('MMMM DD') }}
            </p>

            <div class="my-3 flex flex-col gap-2">
              <div class="flex items-center gap-1">
                <img
                  class="mx-1 h-4 w-4"
                  src="@/assets/origin-marker.svg"
                  alt="origin icon"
                />

                <p class="text-steel-900">
                  {{ truck.origin_search_display_name }}
                </p>
              </div>

              <div class="flex items-center gap-1">
                <img
                  src="@/assets/loads/destination-marker.svg"
                  alt="origin icon"
                />

                <p class="text-steel-900">
                  {{ truck.destination_search_display_name || '-' }}
                </p>
              </div>
            </div>

            <p
              class="steel-900 w-max rounded-lg bg-steel-100 px-2 py-[2px] capitalize"
            >
              {{ truck.posted_equipment_type?.replace(/_/, ' ') }}
            </p>

            <div
              v-if="!isExpired"
              class="mt-3 flex gap-3"
            >
              <ButtonComponent
                class="flex flex-1 justify-center"
                @click="toggleRemoveModal(truck)"
              >
                Remove
              </ButtonComponent>
              <ButtonComponent
                variant="secondary"
                class="flex flex-1 justify-center"
                @click="toggleEditModal(truck)"
              >
                Edit
              </ButtonComponent>
            </div>
          </div>

          <InfiniteScroll @load-more="loadMoretrucks"></InfiniteScroll>

          <div
            v-if="isTrucksRefetching && !is_truck_posting_loading"
            class="pb-12 pt-6"
          >
            <img
              class="loading m-auto h-12 w-12"
              src="@/assets/loading-v2-yellow.svg"
              alt="loading spinner"
            />
          </div>
        </div>
        <div
          v-else
          class="mt-4"
        >
          <p class="text-steel-700">
            {{
              isExpired
                ? 'You have no expired trucks at this time.'
                : 'You have no posted trucks at this time.'
            }}
          </p>
        </div>
      </div>
    </div>

    <remove-capacity-modal
      v-if="isRemoveModalVisible"
      :visible="isRemoveModalVisible"
      :capacity="selectedCapacity"
      @close="toggleRemoveModal"
    ></remove-capacity-modal>
    <post-capacity-modal
      v-if="isEditModalVisible"
      :visible="isEditModalVisible"
      :is-edit="true"
      :capacity="selectedCapacity"
      @close="toggleEditModal"
    ></post-capacity-modal>
  </div>
</template>

<script setup>
import { computed, ref, defineProps, defineEmits } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import dayjs from 'dayjs';
import { ButtonComponent, Pagination, InfiniteScroll } from '@/components';
import {
  CapacityNotification,
  PostCapacityModal,
  RemoveCapacityModal,
} from '@/components/loads';
import { createFilterQuery } from '@/utils/loadFilters.js';

const store = useStore();
const router = useRouter();

const props = defineProps({
  isDesktop: {
    type: Boolean,
    default: false,
  },
  selectedTab: {
    type: String,
  },
});

const emit = defineEmits(['handle-tab-selection']);

const isRemoveModalVisible = ref(false);
const selectedCapacity = ref({});

const toggleRemoveModal = (truck) => {
  isRemoveModalVisible.value = !isRemoveModalVisible.value;

  if (truck) {
    selectedCapacity.value = truck;
  } else {
    selectedCapacity.value = {};
  }
};

const isEditModalVisible = ref(false);
const toggleEditModal = (truck) => {
  isEditModalVisible.value = !isEditModalVisible.value;

  if (truck) {
    selectedCapacity.value = truck;
  } else {
    selectedCapacity.value = {};
  }
};

const isExpired = computed(() => props?.selectedTab === 'Expired');

const truck_postings = computed(() => store.state.loads?.truck_postings);
const truck_postings_pagination = computed(
  () => store.state.loads?.truck_postings_pagination
);

const expired_trucks = computed(
  () => store.state.loads?.expired_truck_postings
);
const expired_truck_pagination = computed(
  () => store.state.loads?.expired_truck_postings_pagination
);

const trucks = computed(() =>
  isExpired.value ? expired_trucks?.value : truck_postings?.value
);
const truck_pagination = computed(() =>
  isExpired.value
    ? expired_truck_pagination?.value
    : truck_postings_pagination?.value
);

const is_truck_posting_loading = computed(() =>
  isExpired.value
    ? store.state.loads.is_expired_truck_posting_loading
    : store.state.loads?.is_truck_postings_loading
);

const isTrucksRefetching = ref(false);

const loadMoretrucks = async () => {
  console.log('Hit infinite scroll');

  if (isExpired?.value) {
    if (expired_truck_pagination?.value?.totalRecords > trucks?.value?.length) {
      isTrucksRefetching.value = true;

      await store.dispatch('loads/getExpiredTruckPostings', {
        page: expired_truck_pagination?.value?.currentPage + 1,
        refetch: true,
      });
    }
    isTrucksRefetching.value = false;
  } else {
    if (
      truck_postings_pagination?.value?.totalRecords > trucks?.value?.length
    ) {
      isTrucksRefetching.value = true;

      await store.dispatch('loads/getTruckPostings', {
        query: '?q[is_active_eq]=1',
        page: truck_postings_pagination?.value?.currentPage + 1,
        refetch: true,
        quiet: true,
      });
    }
  }
};

const onSelectPage = (page) => {
  if (isExpired?.value) {
    store.dispatch('loads/getExpiredTruckPostings', {
      page,
    });
  } else {
    store.dispatch('loads/getTruckPostings', {
      query: '?q[is_active_eq]=1',
      page,
    });
  }
};

const handleTruckSelection = async (truck) => {
  if (!isExpired?.value) {
    const searchObject = {
      ...truck,
      origin: truck?.origin_search_display_name,
      destination: truck?.destination_search_display_name,
      selectedOrigin: truck?.origin_area_id,
      selectedDestination: truck?.destination_area_id,
      trailer: [truck?.posted_equipment_type],
      pickup_start: truck?.start_date,
      pickup_end: dayjs(truck?.start_date).add(1, 'day'),
      origin_deadhead: truck?.origin_deadhead_tolerance,
      destination_deadhead: truck?.destination_deadhead_tolerance,
    };
    await store.commit('loads/setLoads', []);
    await store.commit('loads/setLoadSearchFilters', searchObject);

    await store.dispatch('toast/addToast', {
      component: CapacityNotification,
      props: {
        visible: true,
        title: 'Viewing loads matching capacity for',
        truck,
        isLoadBoardView: true,
      },
    });

    const filterParams = createFilterQuery(
      {
        origin: truck?.origin_area?.name,
        destination: truck?.destination_area?.name,
        selectedOrigin: truck?.origin_area?.name,
        selectedDestination: truck?.destination_area?.name,
        trailer: [truck?.posted_equipment_type],
        pickup_start: truck?.start_date,
        pickup_end: dayjs(truck?.start_date).add(1, 'day'),
      },
      {}
    );

    await router.push({
      name: 'Load Board',
      query: filterParams,
    });
  }
};

const formatPickup = (truck) => {
  if (truck?.end_date) {
    return `${dayjs(truck.start_date).format('MMM DD')} - ${dayjs(truck.end_date).format('MMM DD')}`;
  } else {
    return dayjs(truck.start_date).format('MMM DD');
  }
};
</script>
